/**
 * Compact 3-metric engagement bar used across the emails admin
 * (templates, broadcasts, drips). Shows send / open / click.
 *
 * Renders "אין נתונים" placeholder if stats is falsy or has 0 sent.
 */
import { Send, MailOpen, MousePointerClick } from "lucide-react";

export default function EmailEngagementStats({ stats, testId, compact = false }) {
  if (!stats || stats.sent === 0) {
    return (
      <div
        className={`text-xs text-stone-400 ${compact ? "" : "py-3"}`}
        data-testid={testId ? `${testId}-empty` : undefined}
      >
        {compact ? "—" : "אין נתוני שליחה עדיין"}
      </div>
    );
  }

  const items = [
    { key: "sent",    icon: Send,               label: "נשלחו", value: stats.sent,           sub: null,                                                color: "stone" },
    { key: "opened",  icon: MailOpen,           label: "נפתחו", value: stats.unique_openers, sub: stats.open_rate  != null ? `${stats.open_rate}%`  : null, color: "blue" },
    { key: "clicked", icon: MousePointerClick,  label: "הוקלקו", value: stats.unique_clickers, sub: stats.click_rate != null ? `${stats.click_rate}%` : null, color: "emerald" },
  ];

  const colors = {
    stone:   { icon: "text-stone-500",   val: "text-brand-charcoal", sub: "text-stone-500" },
    blue:    { icon: "text-blue-500",    val: "text-blue-800",       sub: "text-blue-600 font-bold" },
    emerald: { icon: "text-emerald-500", val: "text-emerald-800",    sub: "text-emerald-700 font-bold" },
  };

  if (compact) {
    // Inline horizontal — used inside dense table rows
    return (
      <div className="inline-flex items-center gap-3 text-xs" data-testid={testId}>
        {items.map((it) => {
          const c = colors[it.color];
          const Icon = it.icon;
          return (
            <span key={it.key} className="inline-flex items-center gap-1" title={it.label}>
              <Icon className={`w-3.5 h-3.5 ${c.icon}`} />
              <span className={`font-bold ${c.val}`}>{it.value.toLocaleString("he-IL")}</span>
              {it.sub && <span className={`${c.sub} text-[10px]`}>({it.sub})</span>}
            </span>
          );
        })}
      </div>
    );
  }

  // Card mode — used inside card layouts (templates grid, drip cards)
  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testId}>
      {items.map((it) => {
        const c = colors[it.color];
        const Icon = it.icon;
        return (
          <div key={it.key} className="bg-stone-50 rounded-xl p-2 text-center">
            <Icon className={`w-4 h-4 mx-auto mb-0.5 ${c.icon}`} />
            <div className={`text-base font-[800] leading-none ${c.val}`}>{it.value.toLocaleString("he-IL")}</div>
            {it.sub && <div className={`text-[10px] mt-0.5 ${c.sub}`}>{it.sub}</div>}
            <div className="text-[9px] text-stone-500 uppercase tracking-wide mt-0.5">{it.label}</div>
          </div>
        );
      })}
    </div>
  );
}
